import { DoorOpen, Sparkles, TreePine, UploadCloud } from 'lucide-react';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPrimaryNav } from '../../config/appNav';
import { Badge, Button, Tooltip } from '../../design-system';
import { Metallic } from '../../design-system/icons';
import { useRoomInbox } from '../../features/rooms/RoomInboxContext.jsx';
import { primaryNavKeyFromPathname } from './headerRoute';
import s from './HeaderCenterIconNav.module.css';

const navIcons = {
  home: Sparkles,
  groups: DoorOpen,
  'family-tree': TreePine,
  graves: UploadCloud,
};

export const HeaderCenterIconNav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const isAuth = useSelector((s) => s?.auth?.isAuthorized);
  const user = useSelector((state) => state.user.user);
  const { totalUnreadCount } = useRoomInbox();
  const features = { store: true, familyTree: true }; // TODO: read real feature flags
  const navItems = getPrimaryNav(intl, !!isAuth, features);
  const active = primaryNavKeyFromPathname(pathname);
  const navigateTo = React.useCallback(
    (target) => {
      if (!target) return;
      navigate(target);
    },
    [navigate],
  );

  const rowRef = React.useRef(null);
  const underlineRef = React.useRef(null);

  const handleFamilyTreeNavigate = React.useCallback(() => {
    const identifier = user?.id;
    const viewerPersonId = user?.id ? `u_${user.id}` : null;
    if (!identifier) {
      navigateTo('/family-tree');
      return;
    }
    const rootId = `u_${identifier}`;
    try {
      localStorage.setItem('familyTree.lastId', rootId);
    } catch {
      /* ignore storage errors */
    }

    const params = new URLSearchParams();
    params.set('radius', '3');
    params.set('zoom', '0.85');
    params.set('tab', 'tree');
    params.set('label_strategy', 'expression');
    if (viewerPersonId) params.set('viewer_person_id', viewerPersonId);
    navigateTo(`/family-tree/${encodeURIComponent(rootId)}?${params.toString()}`);
  }, [navigateTo, user?.id]);

  const positionUnderline = React.useCallback(() => {
    const row = rowRef.current;
    const line = underlineRef.current;
    if (!row || !line) return;
    const btn = row.querySelector(`[data-key="${active}"]`);
    if (!btn) return;

    // Token-driven sizing with a max width cap.
    const w = 50;
    const rowRect = row.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const centerX = btnRect.left + btnRect.width / 2 - rowRect.left;

    line.style.width = `${w}px`;
    line.style.transform = `translateX(${centerX - w / 2}px)`;
  }, [active]);

  React.useEffect(() => {
    positionUnderline();
  }, [positionUnderline]);
  React.useEffect(() => {
    const ro = new ResizeObserver(() => positionUnderline());
    if (rowRef.current) ro.observe(rowRef.current);
    window.addEventListener('resize', positionUnderline);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', positionUnderline);
    };
  }, [positionUnderline]);

  return (
    /**
     * Absolutely centered within the header inner container.
     * Does not depend on left/right cluster widths.
     * Visibility on mobile is controlled by surrounding header layout (no media queries here).
     */
    <div className={s.centerSlot} aria-label="Primary navigation">
      <div className={s.centerInner}>
        <div ref={rowRef} className={s.row}>
          {navItems.map(({ key, label, href }) => {
            const Icon = navIcons[key];
            const isDark =
              typeof document !== 'undefined' &&
              (() => {
                const t = document.documentElement.getAttribute('data-theme');
                return t === 'dark' || t === 'night';
              })();
            const baseIconNode = Icon ? (
              isDark ? (
                <Metallic Icon={Icon} variant="silver" size={18} strokeWidth={2.4} />
              ) : (
                <Icon className={`${s.icon} ${active === key ? s.iconActive : ''}`} />
              )
            ) : null;
            const unreadLabel =
              key === 'groups' && totalUnreadCount > 0
                ? intl.formatMessage(
                    {
                      id: 'rooms.navigation.unread_label',
                      defaultMessage:
                        'Rooms, {count, plural, one {# unread message} other {# unread messages}}',
                    },
                    { count: totalUnreadCount },
                  )
                : label;
            const iconNode =
              key === 'groups' ? (
                <Badge
                  count={totalUnreadCount}
                  overflowCount={99}
                  size="small"
                  tone="brand"
                  offset={[2, 3]}
                  slotProps={{ indicator: { className: s.roomUnreadIndicator } }}
                >
                  {baseIconNode}
                </Badge>
              ) : (
                baseIconNode
              );
            const handleClick =
              key === 'family-tree' ? handleFamilyTreeNavigate : () => navigateTo(href);
            return (
              <Tooltip key={key} content={label} placement="bottom" trigger="hover" delay={0}>
                <Button
                  type="text"
                  data-key={key}
                  aria-label={unreadLabel}
                  aria-pressed={active === key}
                  aria-current={active === key ? 'page' : undefined}
                  onClick={handleClick}
                  variant="outlined"
                  color="default"
                  shape="circle"
                  icon={iconNode}
                />
              </Tooltip>
            );
          })}
        </div>
        <div className={s.track}>
          <div ref={underlineRef} className={s.underline} />
        </div>
      </div>
    </div>
  );
};

export default HeaderCenterIconNav;
